import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import type NativeAdUserFlight from './NativeAdUserFlight';
import type PrimeBootSetting from 'owa-service/lib/contract/PrimeBootSetting';
import { getAccountScopeUserConfiguration } from 'mini-common-utils/lib/utils/getAccountScopeUserConfiguration';
import { isAdsEnabledForCCAccounts } from './utils/isAdsEnabledForCCAccounts';
import { isOnboardingComplete } from './isOnboardingComplete';
import { logUserAdConfigLazy } from 'mini-mail-aduser-log';
import {
    getAdMarketPublishGroupCode,
    IsPremiumConsumerUser,
    IsChildConsumerUser,
    IsShadowMailboxUser,
} from 'owa-cookie-targeting/lib/utils/getCommonAdProperties';

export const EMBARGOED_MARKETS = ['ar-ir', 'es-cu', 'ko-kp', 'ar-sy', 'en-su', 'uk-ua'];

let userMarket: string;

export const ADS_NOT_ENABLED_REASON = {
    PremiumUser: 'PremiumUser',
    ShadowMailboxUser: 'ShadowMailboxUser',
    EmbargoedMarket: 'EmbargoedMarket',
    ChildAccount: 'ChildAccount',
    OnboardingCardsIncomplete: 'OnboardingCardsIncomplete',
    AdsNotEnabledForCCAccounts: 'AdsNotEnabledForCCAccounts',
};

interface AdFlightForUser {
    nativeAdUserFlight: NativeAdUserFlight;
    adsNotEnabledReason: string;
}

function calculateAdFlightForUser(): AdFlightForUser {
    userMarket = getAdMarketPublishGroupCode();

    let adsNotEnabledReason = null;
    let nativeAdUserFlight = 1;

    if (IsPremiumConsumerUser()) {
        adsNotEnabledReason = ADS_NOT_ENABLED_REASON.PremiumUser;
    } else if (IsShadowMailboxUser()) {
        const userConfig = getAccountScopeUserConfiguration();
        const primeSettingsItems = userConfig.PrimeSettings?.Items;
        const cloudCacheMailboxPremiumPolicyValue: PrimeBootSetting[] = primeSettingsItems?.filter(
            item => item?.Id === 'CloudCacheMailboxPremiumPolicy'
        );

        if (!isAdsEnabledForCCAccounts()) {
            adsNotEnabledReason = ADS_NOT_ENABLED_REASON.AdsNotEnabledForCCAccounts;
        } else if (cloudCacheMailboxPremiumPolicyValue?.[0]?.Value === true) {
            adsNotEnabledReason = ADS_NOT_ENABLED_REASON.PremiumUser;
        }
    } else if (EMBARGOED_MARKETS.indexOf(userMarket) > -1) {
        adsNotEnabledReason = ADS_NOT_ENABLED_REASON.EmbargoedMarket;
    } else if (IsChildConsumerUser()) {
        adsNotEnabledReason = ADS_NOT_ENABLED_REASON.ChildAccount;
    } else if (!isOnboardingComplete()) {
        adsNotEnabledReason = ADS_NOT_ENABLED_REASON.OnboardingCardsIncomplete;
    }

    if (adsNotEnabledReason) {
        nativeAdUserFlight = 0;
    }

    if (isConsumer()) {
        logUserAdConfigLazy.importAndExecute(
            adsNotEnabledReason === null,
            adsNotEnabledReason,
            userMarket
        );
    }

    return { nativeAdUserFlight, adsNotEnabledReason };
}

// exported for testing purposes
export const globalExports: {
    nativeAdFlight: NativeAdUserFlight;
    calculateAdFlightForUser: () => AdFlightForUser;
    adsNotEnabledReason?: string;
} = {
    nativeAdFlight: undefined,
    calculateAdFlightForUser,
};

/**
 * Get the native ad flight based on the static properties like age, market, paid status.
 * This information will never change during a session and we do not need calculate it again.
 * @returns NativeAdUserFlight enum
 */
export function getNativeFinalFlightForUser(): NativeAdUserFlight {
    // We must calculate the native Ad final flight assigned to a particular user due to the complexity of the native Ad flight. It is not a simple true/false decision
    // We are trying to calculate the native Ad final flight used to determine to be loaded in MailList and NativeAdStore later. This is to avoid multiple calculation in a single user session.
    // The native Ad flight will never change (including enabled or not) during one OWA session.
    // Cache the value so that we do not need to calculate this again and again
    if (typeof globalExports.nativeAdFlight === 'undefined') {
        const { nativeAdUserFlight, adsNotEnabledReason } =
            globalExports.calculateAdFlightForUser();

        globalExports.nativeAdFlight = nativeAdUserFlight;
        globalExports.adsNotEnabledReason = adsNotEnabledReason;
    }

    return globalExports.nativeAdFlight;
}

export const getAdNotEnabledReason = () => globalExports.adsNotEnabledReason;
